import { DynamicIcon } from '@/components/icons/DynamicIcon'
import { Button } from '@/components/shadcn/button'
import { DropdownMenuItem } from '@/components/shadcn/dropdown-menu'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { IconUploadOutline } from '@icons/IconUploadOutline'
import { CellContext } from '@tanstack/react-table'
import { useState } from 'react'
import { DataTableV2UploadTemplateAction } from '../types'
import { TemplateActivityLog } from '../../DataTableComponent/RowActions/TemplateActivityLog'

type UploadTemplateItemProps<T, V> = {
  action: DataTableV2UploadTemplateAction
  info: CellContext<T, V>
  displayAs?: 'dropdown-item' | 'icon-button'
}

export function UploadTemplateMenuItemV2<T, V>({
  action,
  info,
  displayAs = 'dropdown-item',
}: UploadTemplateItemProps<T, V>) {
  const [sheetOpen, setSheetOpen] = useState(false)
  const isIconButton = displayAs === 'icon-button'
  const { entityGUID, templateKey: templateKeyParam } =
    (action.parameterMappings as Record<'entityGUID' | 'templateKey', string>) || {}
  if (!entityGUID || !templateKeyParam) return null

  const entityGuid = (info.row.original as Record<string, string>)[entityGUID]
  const templateKey = (info.row.original as Record<string, string>)[templateKeyParam]

  const trigger = isIconButton ? (
    <TooltipButton tooltip={action.label}>
      <Button variant="ghost" size="icon" className="size-6" disabled={action.isDisabled}>
        <DynamicIcon
          iconName={action.icon || ''}
          lazyLoad={false}
          fallback={<IconUploadOutline className="size-3" />}
        />
        <span className="sr-only">{action.label}</span>
      </Button>
    </TooltipButton>
  ) : (
    <div onClick={(e) => e.stopPropagation()}>
      <DropdownMenuItem onSelect={(e) => e.preventDefault()} disabled={action.isDisabled}>
        <div className="flex items-center gap-2">
          <DynamicIcon
            iconName={action.icon || ''}
            lazyLoad={false}
            className="size-3"
            fallback={<IconUploadOutline className="size-3" />}
          />
          <span>{action.label}</span>
        </div>
      </DropdownMenuItem>
    </div>
  )

  return (
    <SheetWithFormComponent
      title="Upload template"
      description="Upload a template for the entity."
      trigger={trigger}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <TemplateActivityLog entityGuid={entityGuid} templateKey={templateKey} />
    </SheetWithFormComponent>
  )
}
